import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  daysInclusive,
  fmtSofiaDate,
  matchPreset,
  todaySofia,
} from "../../lib/dashboardDates";
import { ownerLabel } from "../../lib/ownerDisplay";

const PRESET_IDS = [
  "today",
  "last_7",
  "last_30",
  "last_90",
  "this_month",
  "last_month",
  "this_year",
];

export const TEAM_UNASSIGNED = "unassigned";

/**
 * Phase 2D.3B.3 — THE global dashboard scope bar: Период · Екип · Отговорник.
 *
 * The period drives the ACTIVITY reporting only; the team and the person drive
 * everything. The team selector is a locked, read-only chip for a Manager (it
 * reads exactly the team it manages) and does not exist at all for a self-scoped
 * actor.
 */
export default function DashboardScopeBar({
  range,
  customFrom,
  customTo,
  setCustomFrom,
  setCustomTo,
  applyPreset,
  applyCustomRange,
  refreshing,
  lastUpdatedAt,
  scope,
  teamValue,
  onTeamChange,
  ownerValue,
  onOwnerChange,
  ownerCandidates,
  currentUserId,
  onRefresh,
}) {
  const { t } = useTranslation();
  const activePreset = useMemo(
    () => matchPreset(range.from, range.to),
    [range.from, range.to],
  );
  const rangeDays = useMemo(
    () => daysInclusive(range.from, range.to),
    [range.from, range.to],
  );

  const lockedTeamName = useMemo(() => {
    if (!scope?.team_locked) return null;
    const team = (scope.teams || []).find((x) => x.id === scope.locked_team_id);
    return team?.name || null;
  }, [scope]);

  const ownerSelectValue =
    ownerValue && ownerValue !== "mine" ? ownerValue : ownerValue === "mine" ? "mine" : "";

  return (
    <div className="qw-filter-bar qw-scope-bar" data-testid="dashboard-scope-bar">
      <div className="scope-row" data-testid="dashboard-filter-bar">
        <span className="scope-label">{t("dashboard.period_label")}</span>
        <div className="presets" role="group" aria-label={t("dashboard.period_label")}>
          {PRESET_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={`preset-btn${activePreset === id ? " active" : ""}`}
              onClick={() => applyPreset(id)}
              data-testid={`preset-${id}`}
            >
              {t(`dashboard.period_${id}`)}
            </button>
          ))}
        </div>
        <div className="custom-range">
          <label htmlFor="dash-from">{t("dashboard.period_from")}</label>
          <input
            id="dash-from"
            type="date"
            className="qw-input date"
            value={customFrom}
            max={customTo || todaySofia()}
            onChange={(e) => setCustomFrom(e.target.value)}
            data-testid="dashboard-from-date"
          />
          <label htmlFor="dash-to">{t("dashboard.period_to")}</label>
          <input
            id="dash-to"
            type="date"
            className="qw-input date"
            value={customTo}
            min={customFrom}
            onChange={(e) => setCustomTo(e.target.value)}
            data-testid="dashboard-to-date"
          />
          <button
            type="button"
            className="qw-btn primary sm"
            onClick={applyCustomRange}
            disabled={!customFrom || !customTo || customTo < customFrom}
            data-testid="dashboard-apply-range"
          >
            {t("dashboard.period_apply")}
          </button>
        </div>
      </div>

      <div className="scope-row scope-filters">
        {scope?.team_locked ? (
          <div className="scope-field" data-testid="dashboard-team-locked">
            <span className="scope-label">{t("dashboard.scope_team_label")}</span>
            <span className="qw-scope-locked" title={t("dashboard.scope_team_locked_hint")}>
              {lockedTeamName || t("dashboard.scope_team_none")}
            </span>
          </div>
        ) : scope?.can_filter_team ? (
          <div className="scope-field">
            <label className="scope-label" htmlFor="dash-team">
              {t("dashboard.scope_team_label")}
            </label>
            <select
              id="dash-team"
              className="qw-select"
              value={teamValue || ""}
              onChange={(e) => onTeamChange(e.target.value || null)}
              data-testid="dashboard-team-select"
            >
              <option value="">{t("dashboard.scope_team_all")}</option>
              {(scope.teams || []).map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
              {scope.allow_unassigned_team && (
                <option value={TEAM_UNASSIGNED}>{t("dashboard.scope_team_unassigned")}</option>
              )}
            </select>
          </div>
        ) : null}

        {scope?.can_filter_owner && (
          <div className="scope-field">
            <label className="scope-label" htmlFor="dash-owner">
              {t("dashboard.scope_owner_label")}
            </label>
            <select
              id="dash-owner"
              className="qw-select"
              value={ownerSelectValue}
              onChange={(e) => onOwnerChange(e.target.value || null)}
              data-testid="dashboard-owner-select"
            >
              <option value="">{t("dashboard.scope_owner_all")}</option>
              <option value="mine">{t("dashboard.owner_filter_mine")}</option>
              {(ownerCandidates || []).map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {ownerLabel(m, t)}
                  {m.user_id === currentUserId ? ` (${t("dashboard.owner_filter_mine")})` : ""}
                </option>
              ))}
              <option value="unassigned">{t("dashboard.scope_owner_unassigned")}</option>
            </select>
          </div>
        )}

        <button
          type="button"
          className="qw-btn ghost sm"
          onClick={onRefresh}
          disabled={refreshing}
          title={t("dashboard.refresh_now")}
          data-testid="dashboard-refresh-button"
        >
          {t("dashboard.refresh_now")}
        </button>
        <div
          className={`refresh-status${refreshing ? " busy" : ""}`}
          data-testid="dashboard-refresh-status"
        >
          <span className="dot" />
          {refreshing
            ? t("dashboard.refreshing")
            : lastUpdatedAt
              ? `${t("dashboard.last_updated")}: ${lastUpdatedAt.toLocaleTimeString("bg-BG")}`
              : t("dashboard.timezone_note")}
        </div>
        <div className="tz-note" data-testid="dashboard-scope-note">
          {fmtSofiaDate(range.from)} – {fmtSofiaDate(range.to)} ({rangeDays}{" "}
          {rangeDays === 1 ? "ден" : "дни"}) · {t("dashboard.scope_note")}
        </div>
      </div>
    </div>
  );
}

export { PRESET_IDS };
